/**
 * What the TikTok settings card shows, as pure functions so the audit rules are
 * testable without rendering. Rules from TikTok's Content Sharing Guidelines
 * (read 2026-11-03): https://developers.tiktok.com/doc/content-sharing-guidelines
 */
import { type TikTokCreatorInfo, lockSettingsToCreator } from './tiktok-creator-info.ts';
import {
  DEFAULT_TIKTOK_POST_SETTINGS,
  type TikTokPostSettings,
  type TikTokPrivacyLevel,
} from './tiktok-post-params.ts';

export const TIKTOK_PRIVACY_LABELS: Record<TikTokPrivacyLevel, string> = {
  PUBLIC_TO_EVERYONE: 'Everyone',
  MUTUAL_FOLLOW_FRIENDS: 'Friends',
  FOLLOWER_OF_CREATOR: 'Followers',
  SELF_ONLY: 'Only me',
};

export type TikTokMediaKind = 'video' | 'photo';

export interface TikTokCardView {
  /** Audience options, exactly what TikTok offers; only SELF_ONLY when unknown. */
  privacyOptions: Array<{
    value: TikTokPrivacyLevel;
    label: string;
    disabledReason: string | null;
  }>;
  interactions: Array<{
    key: 'allowComment' | 'allowDuet' | 'allowStitch';
    label: string;
    checked: boolean;
    disabled: boolean;
    disabledReason: string | null;
  }>;
  commercialLabel: string | null;
  /** Branded content cannot be private, so its box is disabled while "Only me" is chosen. */
  brandedContentDisabledReason: string | null;
  consentText: string;
  /** Why publishing is held, in the card's own words, or null. */
  holdReason: string | null;
}

export const TIKTOK_BRANDED_PRIVATE_REASON = 'Branded content visibility cannot be set to private.';
export const TIKTOK_COMMERCIAL_CHOICE_REASON =
  'You need to indicate if your content promotes yourself, a third party, or both.';

export function tiktokConsentText(settings: TikTokPostSettings): string {
  const branded = settings.commercialContent && settings.brandedContent;
  return branded
    ? "By posting, you agree to TikTok's Branded Content Policy and Music Usage Confirmation."
    : "By posting, you agree to TikTok's Music Usage Confirmation.";
}

export function tiktokCommercialLabel(
  settings: TikTokPostSettings,
  kind: TikTokMediaKind,
): string | null {
  if (!settings.commercialContent) return null;
  const noun = kind === 'video' ? 'video' : 'photo';
  if (settings.brandedContent) return `Your ${noun} will be labeled as "Paid partnership".`;
  if (settings.yourBrand) return `Your ${noun} will be labeled as "Promotional content".`;
  return null;
}

export function buildTikTokCardView(input: {
  settings: TikTokPostSettings | null;
  creatorInfo: TikTokCreatorInfo | null;
  mediaKind: TikTokMediaKind;
}): TikTokCardView {
  const base = input.settings ?? DEFAULT_TIKTOK_POST_SETTINGS;
  const settings = lockSettingsToCreator(base, input.creatorInfo);
  const offered = input.creatorInfo?.privacyLevelOptions ?? [];
  const values: TikTokPrivacyLevel[] = offered.length > 0 ? offered : ['SELF_ONLY'];
  const brandedOn = settings.commercialContent && settings.brandedContent;

  const privacyOptions = values.map((value) => ({
    value,
    label: TIKTOK_PRIVACY_LABELS[value],
    disabledReason: value === 'SELF_ONLY' && brandedOn ? TIKTOK_BRANDED_PRIVATE_REASON : null,
  }));

  const info = input.creatorInfo;
  const interaction = (
    key: 'allowComment' | 'allowDuet' | 'allowStitch',
    label: string,
    creatorOff: boolean,
  ) => ({
    key,
    label,
    checked: settings[key],
    disabled: creatorOff,
    disabledReason: creatorOff ? 'Turned off in your TikTok settings.' : null,
  });
  const interactions = [
    interaction('allowComment', 'Allow comments', info?.commentDisabled === true),
  ];
  if (input.mediaKind === 'video') {
    interactions.push(
      interaction('allowDuet', 'Allow duets', info?.duetDisabled === true),
      interaction('allowStitch', 'Allow stitches', info?.stitchDisabled === true),
    );
  }

  let holdReason: string | null = null;
  if (settings.commercialContent && !settings.yourBrand && !settings.brandedContent) {
    holdReason = TIKTOK_COMMERCIAL_CHOICE_REASON;
  }

  return {
    privacyOptions,
    interactions,
    commercialLabel: tiktokCommercialLabel(settings, input.mediaKind),
    brandedContentDisabledReason:
      settings.privacyLevel === 'SELF_ONLY' ? TIKTOK_BRANDED_PRIVATE_REASON : null,
    consentText: tiktokConsentText(settings),
    holdReason,
  };
}
